// src/components/theme/elements/ThemeLine.js

import React from 'react';
import { Group, Line as KonvaLine, Circle } from 'react-konva';
import PropTypes from 'prop-types';
import CustomColorPicker from '../CustomColorPicker';
import styles from './styles.module.css';
import { clamp, snapToGridFn } from '../CanvasTools';
import { SCREEN_HEIGHT, SCREEN_WIDTH } from '../ScreenProps';


// ThemeLineSettings Component
export const ThemeLineSettings = ({ element, updateElement, openColorModal }) => {

  // Handler for stroke width input change
  const handleStrokeWidthChange = (e) => {
    const strokeWidth = parseInt(e.target.value, 10);
    if (!isNaN(strokeWidth) && strokeWidth > 0) {
      updateElement(element.id, 'strokeWidth', strokeWidth);
    }
  };

  // Handlers for Point1 (Start Point)
  const handlePoint1XChange = (e) => {
    const x = parseInt(e.target.value, 10);
    if (!isNaN(x)) {
      const newPoints = [...element.points];
      newPoints[0] = x;
      updateElement(element.id, 'points', newPoints);
    }
  };

  const handlePoint1YChange = (e) => {
    const y = parseInt(e.target.value, 10);
    if (!isNaN(y)) {
      const newPoints = [...element.points];
      newPoints[1] = y;
      updateElement(element.id, 'points', newPoints);
    }
  };

  // Handlers for Point2 (End Point)
  const handlePoint2XChange = (e) => {
    const x = parseInt(e.target.value, 10);
    if (!isNaN(x)) {
      const newPoints = [...element.points];
      newPoints[2] = x;
      updateElement(element.id, 'points', newPoints);
    }
  };

  const handlePoint2YChange = (e) => {
    const y = parseInt(e.target.value, 10);
    if (!isNaN(y)) {
      const newPoints = [...element.points];
      newPoints[3] = y;
      updateElement(element.id, 'points', newPoints);
    }
  };

  return (
    <div>


      {/* New Settings for Point1 */}
      <div style={{ marginTop: '20px' }}>
        <h5>Point 1 (Start Point)</h5>
        <label>
          X:
          <input
            className={styles.inputNumber}
            type="number"
            min="0"
            max={SCREEN_WIDTH - 1}
            value={element.points[0]}
            onChange={handlePoint1XChange}
            style={{ marginLeft: '10px', width: '60px' }}
          />
        </label>
        <label style={{ display: 'block', marginTop: '10px' }}>
          Y:
          <input
            className={styles.inputNumber}
            type="number"
            min="0"
            max={SCREEN_HEIGHT - 1}
            value={element.points[1]}
            onChange={handlePoint1YChange}
            style={{ marginLeft: '10px', width: '60px' }}
          />
        </label>
      </div>

      {/* New Settings for Point2 */}
      <div style={{ marginTop: '20px' }}>
        <h5>Point 2 (End Point)</h5>
        <label>
          X:
          <input
            className={styles.inputNumber}
            type="number"
            min="0"
            max={SCREEN_WIDTH - 1}
            value={element.points[2]}
            onChange={handlePoint2XChange}
            style={{ marginLeft: '10px', width: '60px' }}
          />
        </label>
        <label style={{ display: 'block', marginTop: '10px' }}>
          Y:
          <input
            className={styles.inputNumber}
            type="number"
            min="0"
            max={SCREEN_HEIGHT - 1}
            value={element.points[3]}
            onChange={handlePoint2YChange}
            style={{ marginLeft: '10px', width: '60px' }}
          />
        </label>
      </div>

      {/* Color */}
      <CustomColorPicker
        label="Color"
        color={element.color}
        onChange={(value) => { updateElement(element.id, 'color', value); }}
      />

      {/* Width */}
      <label style={{ display: 'block', marginTop: '10px' }}>
        Stroke Width:
        <input
          className={styles.inputNumber}
          type="number"
          min="1"
          value={element.strokeWidth || 2}
          onChange={handleStrokeWidthChange}
          style={{ marginLeft: '10px', width: '60px' }}
        />
      </label>

    </div>
  );
};

ThemeLineSettings.propTypes = {
  element: PropTypes.shape({
    id: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    strokeWidth: PropTypes.number,
    points: PropTypes.arrayOf(PropTypes.number).isRequired,
  }).isRequired,
  updateElement: PropTypes.func.isRequired,
  openColorModal: PropTypes.func.isRequired,
};

//////////////////

const ThemeLine = ({
  element,
  selectedElement,
  setSelectedElement,
  gridSize,
  updateElement,
  render, editAllowed,
  snapToGridEnabled,
  scale,
  screenWidth,
  screenHeight
}) => {

  // Determine if this element is selected
  const isSelected = selectedElement && selectedElement.id === element.id;

  // Ref to store initial position
  const initialPosition = React.useRef({ x: 0, y: 0 });

  // Handle dragging the entire line
  const handleGroupDragStart = (e) => {
    const group = e.target;
    initialPosition.current = { x: group.x(), y: group.y() };
  };

  const handleGroupDragEnd = (e) => {
    const group = e.target;
    const newGroupX = snapToGridFn(group.x(), gridSize, snapToGridEnabled);
    const newGroupY = snapToGridFn(group.y(), gridSize, snapToGridEnabled)

    // Calculate delta movement based on initial position
    const deltaX = newGroupX - initialPosition.current.x;
    const deltaY = newGroupY - initialPosition.current.y;

    // Update both points based on delta
    let newPoints = element.points.map((point, index) =>
      index % 2 === 0
        ? snapToGridFn(point + deltaX, gridSize, snapToGridEnabled)
        : snapToGridFn(point + deltaY, gridSize, snapToGridEnabled)
    );

    // clamp positions to screen size
    newPoints = newPoints.map((point, index) =>
      index % 2 === 0
        ? clamp(point, 0, screenWidth - 1)
        : clamp(point, 0, screenHeight - 1)
    );

    newPoints = newPoints.map((point, index) =>
      Math.round(point)
    );

    // Update the element's points
    updateElement(element.id, 'points', newPoints);

    // Reset the group's position to (0, 0)
    group.position({ x: 0, y: 0 });
  };


  // Handle dragging an anchor
  const handleAnchorDragMove = (pointIndex, e) => {
    // Prevent the Group from being dragged when dragging an anchor
    e.cancelBubble = true;

    let newX = e.target.x();
    let newY = e.target.y();

    // Apply snapping if enabled
    newX = snapToGridFn(newX, gridSize, snapToGridEnabled)
    newY = snapToGridFn(newY, gridSize, snapToGridEnabled)

    newX = clamp(newX, 0, screenWidth - 1); // Clamp between 0 and 479
    newY = clamp(newY, 0, screenHeight - 1);

    newX = Math.round(newX);
    newY = Math.round(newY);

    const newPoints = [...element.points];
    newPoints[pointIndex] = newX;
    newPoints[pointIndex + 1] = newY;

    updateElement(element.id, 'points', newPoints);
  };

  return (
    <Group
      x={0} // No offset; positions are absolute within the Stage
      y={0}
      draggable={!render && editAllowed}
      onClick={() => setSelectedElement(element)}
      onDragStart={handleGroupDragStart}
      onDragEnd={handleGroupDragEnd}
    >
      {/* Line */}
      <KonvaLine
        points={element.points}
        stroke={element.color}
        strokeWidth={element.strokeWidth || 2}
      />

      {/* Start Anchor */}
      {!render && (
        <Circle
          x={clamp(element.points[0], 0, screenWidth - 1)}
          y={clamp(element.points[1], 0, screenHeight - 1)}
          radius={3}
          stroke="#1976d2"
          strokeWidth={0.5}
          fill="transparent"
          dash={isSelected ? [1, 1] : [0, 0]}
          draggable={true}
          onDragMove={(e) => handleAnchorDragMove(0, e)}
          onDragStart={(e) => {
            e.cancelBubble = true;
          }} // Prevent Group drag
          onDragEnd={(e) => {
            e.cancelBubble = true;
          }}
        />
      )}

      {/* End Anchor */}
      {!render && (
        <Circle
          x={clamp(element.points[2], 0, screenWidth - 1)}
          y={clamp(element.points[3], 0, screenHeight - 1)}
          radius={3}
          stroke="#1976d2"
          strokeWidth={0.5}
          fill="transparent"
          dash={isSelected ? [1, 1] : [0, 0]}
          draggable={true}
          onDragMove={(e) => handleAnchorDragMove(2, e)}
          onDragStart={(e) => {
            e.cancelBubble = true;
          }} // Prevent Group drag
          onDragEnd={(e) => {
            e.cancelBubble = true;
          }}
        />
      )}
    </Group>
  );
};

ThemeLine.propTypes = {
  element: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    strokeWidth: PropTypes.number,
    usage: PropTypes.string,
    points: PropTypes.arrayOf(PropTypes.number).isRequired,
  }).isRequired,
  setSelectedElement: PropTypes.func.isRequired,
  updateElement: PropTypes.func.isRequired,
  render: PropTypes.bool.isRequired, // Controls visibility of anchors
  snapToGridEnabled: PropTypes.bool.isRequired, // New PropType
};

export default ThemeLine;
